import { useState } from "react";
import { Form, Input, Button, Checkbox, Row, Col, Image, Layout, Divider } from "antd";
import { GoogleLogin } from "@react-oauth/google";
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import LoginBackground from "../assets/login-background.png";
import LoginLogo from "../assets/mongerlab-login-logo.png";
import { Header } from "antd/es/layout/layout";
import { completeLoginSession } from "../utils/sessionUtils";
import { openNotification } from "../utils";

interface FormValues {
  username: string;
  password: string;
  remember: boolean;
}

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const Login: React.FC<any> = ({ setIsLoggedIn, isLoggedIn }) => {
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const dark = localStorage.getItem("darkMode");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onFinish = (values: FormValues) => {
    setLoading(true);
    AuthService.login({
      username: values.username,
      password: values.password,
    })
      .then((response) => {
        completeLoginSession(response, navigate, setIsLoggedIn, isLoggedIn);
      })
      .catch((error) => {
        const message = error?.response?.data?.message || "Giriş başarısız";
        openNotification("error", error?.response?.status || "Error", message);
      })
      .finally(() => setLoading(false));
  };

  const handleGoogleSuccess = (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) {
      openNotification("error", "Error", "Google kimlik doğrulaması başarısız");
      return;
    }

    setGoogleLoading(true);
    AuthService.googleLogin({ idToken: credentialResponse.credential })
      .then((response) => {
        completeLoginSession(response, navigate, setIsLoggedIn, isLoggedIn);
      })
      .catch((error) => {
        const message = error?.response?.data?.message || "Google ile giriş başarısız";
        openNotification("error", error?.response?.status || "Error", message);
      })
      .finally(() => setGoogleLoading(false));
  };

  return (
    <Row justify="center" align="middle">
      <Col xs={0} sm={0} md={12} lg={12} xl={12} span={12}>
        <Layout style={{ height: "100vh", backgroundColor: "#EFF3F6" }}>
          <Header style={{ backgroundColor: "#EFF3F6" }}>
            <Image
              style={{ marginTop: "4.5em", width: 211 }}
              preview={false}
              src={LoginLogo}
              alt="Mongerlab"
            />
          </Header>
          <Row style={{ height: "80vh" }} align={"middle"} justify={"center"}>
            <Col>
              <Image
                style={{ width: "50vw" }}
                preview={false}
                src={LoginBackground}
              />
            </Col>
          </Row>
        </Layout>
      </Col>
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={12}
        xl={12}
        span={12}
        className="loginRight"
      >
        <div style={{ maxWidth: "400px", margin: "0 auto", padding: "20px" }}>
          <Row justify={"center"} align={"middle"}>
            <Col xs={12} sm={24} md={0} lg={0} xl={0} span={0}>
              <Image
                style={{ marginTop: "30px", width: 211 }}
                preview={false}
                src={LoginLogo}
                alt="Mongerlab"
              />
            </Col>
          </Row>
          <h3
            style={{ fontSize: "32px", color: "#1J1E1B", margin: "1em 0 0 0" }}
          >
            Login
          </h3>
          <p
            style={{
              fontSize: "16px",
              color: dark ? "#fff" : "#606676",
              margin: "1em 0 2em 0",
            }}
          >
            Welcome back, please login to your account.
          </p>
          <Form
            name="normal_login"
            className="login-form"
            initialValues={{ remember: true }}
            onFinish={onFinish}
          >
            <b>Username:</b>
            <Form.Item
              name="username"
              rules={[
                { required: true, message: "Please input your Username!" },
              ]}
            >
              <Input
                size="large"
                prefix={<UserOutlined className="site-form-item-icon" />}
                placeholder="Username"
              />
            </Form.Item>
            <b>Password:</b>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please input your Password!" },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="Password"
                iconRender={(visible) =>
                  visible ? (
                    <EyeTwoTone onClick={togglePasswordVisibility} />
                  ) : (
                    <EyeInvisibleOutlined onClick={togglePasswordVisibility} />
                  )
                }
              />
            </Form.Item>
            <Form.Item>
              <Row justify={"space-between"}>
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>Remember me</Checkbox>
                </Form.Item>
              </Row>
            </Form.Item>
            <Form.Item>
              <Button
                style={{ width: "100%", height: "3.3em" }}
                type="primary"
                htmlType="submit"
                className="login-form-button"
                loading={loading}
                disabled={googleLoading}
              >
                Sign in
              </Button>
            </Form.Item>
          </Form>
          {googleClientId && (
            <>
              <Divider plain style={{ margin: "1.5em 0" }}>
                veya
              </Divider>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() =>
                    openNotification("error", "Error", "Google ile giriş başarısız")
                  }
                  text="signin_with"
                  width="100%"
                />
              </div>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default Login;
